import {
  TuiAlertModule,
  TuiButtonModule,
  TuiDialogModule,
  TuiDialogService,
  TuiRootModule,
} from '@taiga-ui/core';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { LanguageService } from '@shared/services/language.service';
import { GoogleAuthService } from '@shared/services/google-auth.service';
import { catchError, filter, of, switchMap, take, tap } from 'rxjs';
import { RouteTitleTranslateService } from '@shared/services/route-title-translate.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { Store } from '@ngxs/store';
import { AlertService } from '@shared/services/alert.service';
import { SetUserPersonalData, SignIn } from '@store/auth/auth.actions';
import { AuthService } from '@shared/services/auth.service';
import { FooterComponent } from './core/footer/footer.component';
import { NavbarComponent } from './core/navbar/navbar.component';
import { VersionAvailableComponent } from './core/components/version-available/version-available.component';
import { RouteNames } from './route-names';
import { SignInResponse } from './types/auth';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    NavbarComponent,
    FooterComponent,
    TuiRootModule,
    TuiDialogModule,
    TuiAlertModule,
    TuiButtonModule,
    TranslocoPipe,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = 'come-on';

  constructor(
    private readonly swUpdate: SwUpdate,
    private readonly languageService: LanguageService,
    private readonly googleAuthService: GoogleAuthService,
    private readonly routeTitleTranslateService: RouteTitleTranslateService,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
    private readonly dialogs: TuiDialogService,
    private readonly store: Store,
    private readonly alert: AlertService,
    private readonly auth: AuthService,
  ) {}

  showNotification() {
    return this.dialogs.open(
      new PolymorpheusComponent(VersionAvailableComponent),
      {
        size: 's',
        closeable: false,
        dismissible: false,
      },
    );
  }

  checkNewVersion() {
    this.swUpdate?.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        switchMap(() => this.showNotification()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.checkNewVersion();
    this.signInWithGoogleIfPossible();
    this.routeTitleTranslateService.init();
  }

  signInWithGoogleIfPossible() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        take(1),
        switchMap(() =>
          this.googleAuthService.trySignInWithQueryParams().pipe(
            filter((response): response is SignInResponse => response !== null),
            tap(response => {
              this.store.dispatch(new SignIn(response));
            }),
            switchMap(() =>
              this.auth.getPersonalData().pipe(
                tap(data => {
                  this.store.dispatch(new SetUserPersonalData(data));
                  this.router.navigate([RouteNames.HOME]);
                }),
              ),
            ),
            catchError(err => {
              this.alert.showError(err.error);
              return of(null);
            }),
          ),
        ),
      )
      .subscribe();
  }
}
